import { showDeleteConfirmation } from '@modules/common';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { CalendarAPI } from '@business/api/calendar';
import { UserId, UserSocialId } from '@business/entities/account';
import { CalendarResponse, CalendarEventResponse, EventMeta } from '@business/entities/calendar';
import { map, switchMap, tap } from 'rxjs/operators';
import { RecursivePartial } from '@core/utils/react';
import { forkJoin, combineLatest, of } from 'rxjs';
import { R } from '@core/utils/r';

export class CalendarBloc extends BaseBloc {
  private readonly calendarRepo = new Repository<CalendarResponse>({
    getItemId: calendar => calendar.id,
  });
  private readonly eventsRepo = new Repository<CalendarEventResponse>({
    getItemId: event => event.grant_id + '--' + event.id,
  });

  readonly calendars$ = this.calendarRepo.items$;
  readonly selectCalendar = this.calendarRepo.selectItem;

  events$ = this.eventsRepo.items$;

  onReset() {
    this.calendarRepo.reset();
  }

  list = (socialId: UserId) => {
    return CalendarAPI.list(socialId).pipe(this.calendarRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_brands_error') }));
  };

  events = (socialIds: UserSocialId[], startDate: number, endDate: number, participent?: string) => {
    return of([]).pipe(
      tap(() => {
        const ids = this.eventsRepo.items
          .filter(event => event.when.start_time >= startDate && event.when.end_time <= endDate)
          .map(event => event.grant_id + '--' + event.id);
        // this.eventsRepo.removeMany(ids);
      }),
      tap(() => {
        const allObs = socialIds.map(socialId => CalendarAPI.events([socialId], startDate, endDate, participent));
        allObs.forEach(obs => {
          obs.pipe(this.eventsRepo.ops.upsertMany(item => ({ item }))).subscribe();
        });
      }),
    );
  };

  resetEvents = () => {
    this.eventsRepo.reset();
  };

  addEvent = (socialId: UserSocialId, event: RecursivePartial<CalendarEventResponse>, meta?: EventMeta) => {
    return CalendarAPI.addEvent(socialId, event, meta).pipe(
      this.eventsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_brands_error') }),
    );
  };

  editEvent = (socialId: UserSocialId, event: RecursivePartial<CalendarEventResponse>, eventId: string) => {
    return CalendarAPI.editEvent(socialId, event, eventId).pipe(
      this.eventsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_brands_error') }),
    );
  };

  deleteEvent = (calendarId: string, eventId: string) => {
    return showDeleteConfirmation('Cancel event', 'Do you really want to cancel this event?').pipe(
      switchMap(() => CalendarAPI.deleteEvent(calendarId, eventId)),
      this.eventsRepo.ops.removeOne(() => `${calendarId}--${eventId}`),
      handleMessage({ error: requestMessage('fetch_brands_error') }),
    );
  };
}
